import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import logo from "../images/dc_logo.png";
import "./../mentorDetails/MentorDetails.css";

// const BASE_URL = "http://localhost:8000";
const BASE_URL = "https://idealpursuits.co.in/api";

function ExpertDetails() {
    const { expertId } = useParams();
    const [isChecked, setIsChecked] = useState(false);
    const [expert, setExpert] = useState(null);
    const [scrollY, setScrollY] = useState(0);
    const [meetingLink, setMeetingLink] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTime, setSelectedTime] = useState("");
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [popupType, setPopupType] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const { isLogged, userData } = useAuth();
    const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
    const [dateTimeMap, setDateTimeMap] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        const fetchExpertDetails = async () => {
            try {
                // Fetch expert details
                const expertResponse = await axios.get(`${BASE_URL}/expert/getExpertDetails.php?expertId=${expertId}`);
                const expertData = expertResponse.data;
                setExpert(expertData);

                // Fetch expert dates
                const datesResponse = await axios.get(`${BASE_URL}/expert/getExpertDatesForUser.php?expertId=${expertId}`);
                const datesData = datesResponse.data;

                // Get the current date and time
                const currentTime = new Date().getTime();

                // Process dates and times
                const dateTimeMap = {}; // Object to store times for each date
                const availableDates = [];

                datesData.forEach((entry) => {
                    const { id, date, times: time } = entry;
                    const entryDateTime = new Date(`${date}T${time}`).getTime();

                    // Filter for upcoming dates and times at least 12 hours ahead
                    if (entryDateTime > currentTime + 12 * 60 * 60 * 1000) {
                        if (!dateTimeMap[date]) {
                            dateTimeMap[date] = [];
                            availableDates.push(date); // Add date only once
                        }
                        dateTimeMap[date].push({ id, time }); // Retain id along with time
                    }
                });

                // Sort dates in ascending order
                availableDates.sort((a, b) => new Date(a) - new Date(b));

                // Update state
                setAvailableDates(availableDates);
                setDateTimeMap(dateTimeMap); // Store date-time mapping with IDs

                // Set default selected date and time
                if (availableDates.length > 0) {
                    setSelectedDate(availableDates[0]);
                    const sortedTimes = dateTimeMap[availableDates[0]].sort((a, b) => a.time.localeCompare(b.time));
                    setAvailableTimes(sortedTimes);
                    setSelectedTime(sortedTimes[0]);
                }
            } catch (error) {
                console.error("Error fetching expert details or dates:", error);
            }
        };

        fetchExpertDetails();
    }, [expertId]);

    const convertTo12HrFormat = (time) => {
        const [hour, minute] = time.split(":");
        let newHour = hour % 12;
        newHour = newHour ? newHour : 12;
        const ampm = hour >= 12 ? "PM" : "AM";
        return `${newHour}:${minute} ${ampm}`;
    };

    const initializeRazorpay = () => {
        return new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const handlePayment = async () => {
        try {
            setIsProcessing(true);
            const res = await initializeRazorpay();

            if (!res) {
                alert("Razorpay SDK failed to load");
                setIsProcessing(false);
                return;
            }

            // Create order
            const orderResponse = await axios.post(`${BASE_URL}/create-meeting-order.php`, {
                amount: expert.meetingCharge * 100, // Convert to paise
                currency: "INR",
                tutorId: expert.expertId,
                userId: userData.userId,
                meetingDate: selectedDate,
                meetingTime: convertTo12HrFormat(selectedTime.time)
            });

            const options = {
                key: "rzp_live_126ap0g6lfYF7b",
                amount: expert.meetingCharge * 100,
                currency: "INR",
                name: "The Ideal Pursuits",
                description: `Session with ${expert.name}`,
                order_id: orderResponse.data.orderId,
                handler: async (response) => {
                    try {
                        // Verify payment
                        const verifyResponse = await axios.post(`${BASE_URL}/verify-meeting-payment.php`, {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature
                        });

                        if (verifyResponse.data.status === "success") {
                            await handlePaymentSuccess();
                        } else {
                            alert("Payment verification failed");
                        }
                    } catch (error) {
                        console.error("Payment verification error:", error);
                        alert("Payment verification failed");
                    }
                },
                prefill: {
                    name: userData.name,
                    email: userData.email
                },
                theme: {
                    color: "#3399cc"
                },
                modal: {
                    ondismiss: () => {
                        setIsProcessing(false);
                    }
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();
        } catch (error) {
            console.error("Payment initiation error:", error);
            alert("Failed to initiate payment");
            setIsProcessing(false);
        }
    };

    const handleContinue = () => {
        if (selectedDate && selectedTime) {
            if (!isLogged) {
                navigate("/login", { state: { returnUrl: `/professional-profile/${expertId}` } });
            } else {
                setPopupType("terms");
            }
        } else {
            alert("Please select a date and time.");
        }
    };


    const handleCheckboxChange = (e) => {
        setIsChecked(e.target.checked);
    };

    const handlePaymentSuccess = async () => {
        if (isChecked) {
            try {
                setIsPaymentProcessing(true);
                setPopupType(null);

                const response = await axios.post(`${BASE_URL}/meetings.php`, {
                    userId: userData.userId,
                    tutorId: expert.expertId,
                    tutorType: 'expert',
                    tutorName: expert.name,
                    userName: userData.name,
                    meetingDate: selectedDate,
                    bookedDateId: selectedTime.id,
                    meetingTime: convertTo12HrFormat(selectedTime.time),
                    meetingCharge: expert.meetingCharge
                });

                await axios.post(`${BASE_URL}/setExpertDateBooked.php`, {
                    id: selectedTime.id
                });

                await axios.post(`${BASE_URL}/setExpertMeetingAttended.php`, {
                    userId: userData.userId
                });

                if (response.data.status === "success") {
                    setIsPaymentProcessing(false);
                    setMeetingLink(response.data.meetingLink);
                    setPopupType("success");
                } else {
                    setIsPaymentProcessing(false);
                    alert("Failed to schedule the meeting: " + response.data.message);
                }
            } catch (error) {
                setIsPaymentProcessing(false);
                console.error("Error scheduling meeting:", error);
                alert("An error occurred. Please try again.");
            }
        }
    };

    if (!expert) return <div>Loading...</div>;

    const headerStyle = {
        gap: `${Math.min(150, scrollY)}px`,
        marginTop: `${Math.min(15, scrollY / 5)}px`,
        opacity: `${1 - Math.min(scrollY / 300, 0.2)}`,
        width: `${100 - Math.min(scrollY / 5, 20)}%`,
        borderRadius: `${Math.min(15, scrollY / 10)}px`,
    };

    const renderTermsButtons = () => (
        <>
            <button
                className="close-popup-button"
                onClick={() => setPopupType(null)}
                disabled={isProcessing}
            >
                Close
            </button>
            <button
                className={`agree-button ${isProcessing ? 'processing' : ''}`}
                onClick={handlePayment}
                disabled={!isChecked || isProcessing}
            >
                {isProcessing ? 'Processing...' : 'Proceed to Payment'}
            </button>
        </>
    );

    const formatTo12Hr = (time) => {
        const [hour, minute] = time.split(":");
        const formattedHour = hour % 12 || 12;
        const period = hour >= 12 ? "PM" : "AM";
        return `${formattedHour}:${minute} ${period}`;
    };

    const formatToDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    return (
        <div>
            {isPaymentProcessing && (
                <div className="booking-popup-modal">
                    <div className="loading-popup">
                        <div className="loading-spinner"></div>
                        <p>Processing your booking...</p>
                        <p>Please don't close this window</p>
                    </div>
                </div>
            )}
            
            <header className="header" style={headerStyle}>
                <div className="logo-area">
                    <img className="logo-image" src={logo} alt="logo" />
                    <div className="logo" onClick={() => { navigate('/') }}>The Ideal Pursuits</div>
                </div>
            </header>
            <div className="mentor-details-page">
                <div className="mentor-info-container">
                    {expert.profileImageName ? (
                        <img
                            src={`${BASE_URL}/images/profile/${expert.profileImageName}`}
                            alt={expert.name}
                            className="profile-image"
                        />
                    ) : (
                        <p>No Image</p>
                    )}
                    <h2>{expert.name}</h2>
                    <p>{expert.profession}</p>
                    <p>{expert.category}</p>
                    <p>{expert.experience} years of experience</p>
                    <p>Charges: ₹{expert.meetingCharge} per session</p>
                </div>
                <div className="booking-details-container">
                    <h3>Book a Session</h3>
                    <p><strong>When do you want to meet?</strong></p>
                    <div className="booking-dates">
                        {availableDates.map((date, index) => (
                            <div
                                key={index}
                                className={`booking-date-item ${selectedDate === date ? "selected" : ""}`}
                                onClick={() => {
                                    setSelectedDate(date);
                                    setAvailableTimes(dateTimeMap[date].sort()); // Update times when date is selected
                                    setSelectedTime(dateTimeMap[date][0]);       // Set the first time as default
                                }}
                            >
                                {formatToDate(date)}
                            </div>
                        ))}
                    </div>

                    <p><strong>Select your preferred time:</strong></p>
                    <div className="booking-times">
                        {availableTimes.map((timeObj, index) => (
                            <div
                                key={timeObj.id}
                                className={`booking-time-item ${selectedTime?.time === timeObj.time ? "selected" : ""}`}
                                onClick={() => setSelectedTime(timeObj)}
                            >
                                {formatTo12Hr(timeObj.time)}
                            </div>
                        ))}
                    </div>

                    <button className="continue-to-payment-button" onClick={handleContinue}>Continue to Payment</button>
                </div>
            </div>

            {popupType === "terms" && (
                <div className="booking-popup-modal">
                    <div className="popup-content">
                        {/* <h2>How to Make the Most of Your Meeting</h2> */}
                        {/* <ul>
                            <li>Prepare specific questions or topics in advance.</li>
                            <li>Research the expert’s background to tailor your discussion.</li>
                            <li>Focus on actionable advice and ask for practical insights.</li>
                            <li>Take notes during the session for future reference.</li>
                            <li>Pay close attention to the expert's responses and ask clarifying questions.</li>
                            <li>Follow up with the expert if they allow post-meeting communication.</li>
                            <li>Take some time to reflect on the key takeaways and how you can apply them.</li>
                        </ul> */}
                        <h3>Questions you can ask the Industry Expert to get the most out of the meeting:</h3>
                        <ul>
                            <li>What are the important skills to develop to advance in this career more effectively?</li>
                            <li>What are the steps to create a plan for career advancement?</li>
                            <li>What are the risks in this career?</li>
                            <li>How to create and track progress of my goals. Any tips to do so?</li>
                            <li>How do you manage stress at work?</li>
                            <li>How do you have a work-life balance in this career and what are the methods that you use to achieve it?</li>
                            <li>Do you use any project management tools to track progress of individual goals?</li>
                            <li>What are the important technological tools I need to learn in this field?</li>
                            <li>How can I improve my interpersonal skills?</li>
                            <li>The best career advice you have received?</li>
                            <li>What do you love the most about your career?</li>
                            <li>What are the challenges in working in this field?</li>
                            <li>Any daily routines that you follow to achieve success in your career?</li>
                            <li>What skills are beneficial for this field?</li>
                            <li>Where should I improve that will help me to achieve the goals that I have shared with you?</li>
                            <li>How can I improve my communication?</li>
                            <li>What are the challenges and obstacles that I can expect on my career path and how to overcome them?</li>
                            <li>How to find my skill gaps and how can I approach them?</li>
                            <li>What are the networking events or conferences you recommend for professional growth?</li>
                            <li>Are there any setbacks or challenges in professional life and how to overcome them from your personal experience?</li>
                            <li>How to pursue my career goals? What are the strategies I need to follow?</li>
                            <li>Do you think artificial intelligence can take over this field in the near future?</li>
                            <li>Any books you suggest to enhance my career?</li>
                        </ul>
                        <h3>Terms & Conditions</h3>
                        <p>
                            You agree to be connected with the expert on the desired date and time.
                        </p>
                        <p><a href="/#/user-expert-tips" style={{ color: "blue" }} className="terms-link" target="_blank" rel="noopener noreferrer">Click here for tips!</a></p>

                        <div className="terms-container">
                            <input
                                type="checkbox"
                                id="agreeTerms"
                                className="terms-checkbox"
                                checked={isChecked}
                                onChange={handleCheckboxChange}
                                disabled={isProcessing}
                            />
                            <label htmlFor="agreeTerms" className="terms-label">
                                I agree to the
                                <a href="/#/professional-terms-and-conditions" className="terms-link" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>and
                                <a href="/#/privacy-policy" className="terms-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                            </label>
                        </div>
                        {renderTermsButtons()}
                    </div>
                </div>
            )}

            {popupType === "success" && (
                <div className="booking-popup-modal">
                    <div className="popup-content">
                        <h2>Payment Successful</h2>
                        <p>
                            Your meeting is scheduled with {expert.name} on {formatToDate(selectedDate)} at {convertTo12HrFormat(selectedTime.time)}.
                        </p>
                        <p>
                            You can find the meeting link in your <a href='/#/profile'>profile</a> section.
                        </p>
                        <button className="profile-button" onClick={() => navigate("/profile")}>
                            Go to Profile
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ExpertDetails;
